/** @jsx jsx */

import * as React from "react";
import { css, jsx } from "@emotion/core";
import { SVGWrapper } from "Views/presentational/svg_wrapper";
import { DESIGN_TOKEN } from "Config/design_token";

/** Properties needed to render the hamburger button. */
export type HamburgerProps = {
  /** If the button is controlling an open menu or a closed one. */
  isOpen: boolean;
  /** Method that fires when the hamburger is clicked. */
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

function Hamburger(props: HamburgerProps): JSX.Element {
  const { isOpen, onClick } = props;

  return (
    <button
      aria-expanded={isOpen}
      aria-label="Toggle navigation"
      css={css`
        display: block;
        position: fixed;
        z-index: 99;
        top: 1rem;
        left: ${isOpen ? `calc(100% - 4rem)` : `1rem`};
        width: 3rem;
        height: 3rem;
        padding: 0.5rem;
        flex-direction: column;
        justify-content: space-between;
        background-color: ${DESIGN_TOKEN.colors.swatch_3};
        border: thin solid ${DESIGN_TOKEN.colors.swatch_1};
        transition: left 0.3s ease;
        &:hover {
          cursor: pointer;
        }
      `}
      onClick={onClick}
    >
      <SVGWrapper
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        css={css`
          display: block;
          padding: 0rem;
          width: 100%;
          height: 100%;
        `}
      >
        <React.Fragment>
          <rect
            x="0"
            y="0"
            width="100"
            height="16"
            fill={`${DESIGN_TOKEN.colors.swatch_1}`}
            stroke="none"
            css={css`
              position: relative;
              transform-origin: 50% 50%;
              ${isOpen && "transform: rotate(45deg) translate(0rem, 2.5rem);"}
              transition: rotate 0.3s ease;
            `}
          />
          <rect
            x="0"
            y="42"
            width="100"
            height="16"
            fill={`${DESIGN_TOKEN.colors.swatch_1}`}
            stroke="none"
            css={css`
              opacity: ${isOpen ? "0" : "1"};
              transition: opacity 0.3s ease;
            `}
          />
          <rect
            x="0"
            y="84"
            width="100"
            height="16"
            fill={`${DESIGN_TOKEN.colors.swatch_1}`}
            stroke="none"
            css={css`
              position: relative;
              transform-origin: 50% 50%;
              ${isOpen && "transform: rotate(-45deg) translate(0rem, -2.5rem);"}
              transition: rotate 0.3s ease;
            `}
          />
        </React.Fragment>
      </SVGWrapper>
    </button>
  );
}

const hamburgerMemo = React.memo<HamburgerProps>(Hamburger);
export { hamburgerMemo as Hamburger };
